import {
  Transaction,
  Asset,
  Debt,
  ExpenseBreakdown,
  RunwayProjection,
  IncomeSource,
} from "../types";
import { startOfMonth, endOfMonth } from "date-fns";

export const calculateRunway = (
  balance: number,
  monthlyExpenses: number
): number => {
  if (monthlyExpenses <= 0) return Infinity;
  return balance / monthlyExpenses;
};

export const calculateMonthlyIncome = (
  transactions: Transaction[],
  userId: string,
  incomeSources: IncomeSource[] = []
): number => {
  const now = new Date();
  const startOfCurrentMonth = startOfMonth(now);
  const endOfCurrentMonth = endOfMonth(now);

  // Calculate from active income sources (primary source)
  const activeSources = incomeSources.filter(
    (is) => is.userId === userId && is.isActive
  );
  const sourceIncome = activeSources.reduce((sum, is) => {
    // Convert to monthly amount based on frequency
    let monthlyAmount: number;
    switch (is.frequency) {
      case "monthly":
        monthlyAmount = is.amount;
        break;
      case "weekly":
        monthlyAmount = is.amount * 4.33; // Average weeks per month
        break;
      case "biweekly":
        monthlyAmount = is.amount * 2.17; // Average bi-weekly periods per month
        break;
      case "yearly":
        monthlyAmount = is.amount / 12;
        break;
      default:
        monthlyAmount = is.amount;
    }
    console.log(
      `Income Source: ${is.name} - ${is.amount} ${is.frequency} = ${monthlyAmount} monthly`
    );
    return sum + monthlyAmount;
  }, 0);

  // Calculate from transactions (only for one-time income, not recurring)
  // Filter out transactions that correspond to active income sources
  const activeSourceNames = activeSources.map((is) => is.name.toLowerCase());

  const currentMonthTransactions = transactions.filter(
    (t) =>
      t.userId === userId &&
      t.type === "income" &&
      new Date(t.date) >= startOfCurrentMonth &&
      new Date(t.date) <= endOfCurrentMonth
  );

  const transactionIncome = currentMonthTransactions
    .filter((t) => {
      // Only include transactions that don't correspond to active income sources
      // Check if transaction description or category matches any active income source name
      const isRecurringIncome = activeSourceNames.some((sourceName) => {
        const transactionText = `${t.description} ${t.category}`.toLowerCase();
        return transactionText.includes(sourceName);
      });

      if (!isRecurringIncome) {
        console.log(`One-time Income: ${t.description} - ${t.amount}`);
      } else {
        console.log(
          `Skipping recurring income transaction: ${t.description} - ${t.amount}`
        );
      }

      return !isRecurringIncome;
    })
    .reduce((sum, t) => sum + t.amount, 0);

  const totalIncome = sourceIncome + transactionIncome;
  return totalIncome;
};

export const calculateMonthlyExpenses = (
  transactions: Transaction[],
  userId: string
): number => {
  const now = new Date();
  const startOfCurrentMonth = startOfMonth(now);
  const endOfCurrentMonth = endOfMonth(now);

  // First try to get current month expenses
  const currentMonthExpenses = transactions
    .filter(
      (t) =>
        t.userId === userId &&
        t.type === "expense" &&
        t.category !== "Debt" && // Exclude debt transactions
        new Date(t.date) >= startOfCurrentMonth &&
        new Date(t.date) <= endOfCurrentMonth
    )
    .reduce((sum, t) => sum + t.amount, 0);

  // If current month has expenses, use that
  if (currentMonthExpenses > 0) {
    return currentMonthExpenses;
  }

  // Otherwise, calculate total of last 3 months (as monthly estimates)
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const recentExpenses = transactions
    .filter(
      (t) =>
        t.userId === userId &&
        t.type === "expense" &&
        t.category !== "Debt" && // Exclude debt transactions
        new Date(t.date) >= threeMonthsAgo
    )
    .reduce((sum, t) => sum + t.amount, 0);

  // If we have recent expenses, use the total (they're already monthly estimates)
  if (recentExpenses > 0) {
    return recentExpenses;
  }

  // If no recent expenses, return 0
  return 0;
};

export const calculateCurrentBalance = (
  transactions: Transaction[],
  userId: string,
  assets: Asset[] = [],
  debts: Debt[] = []
): number => {
  // Calculate balance from transactions
  const transactionBalance = transactions
    .filter((t) => t.userId === userId)
    .reduce((balance, t) => {
      return t.type === "income" ? balance + t.amount : balance - t.amount;
    }, 0);

  // Add only liquid assets (including partner assets that are added to the assets array)
  const liquidAssets = assets
    .filter((a) => a.isLiquid)
    .reduce((sum, a) => sum + a.balance, 0);

  // Subtract debts
  const totalDebts = debts
    .filter((d) => d.userId === userId)
    .reduce((sum, d) => sum + d.balance, 0);

  return transactionBalance + liquidAssets - totalDebts;
};

export const calculateTotalAssets = (
  assets: Asset[],
  userId: string
): number => {
  return assets.reduce((sum, a) => sum + a.balance, 0);
};

export const calculateLiquidAssets = (
  assets: Asset[],
  userId: string
): number => {
  return assets
    .filter((a) => a.isLiquid)
    .reduce((sum, a) => sum + a.balance, 0);
};

export const calculateExpenseBreakdown = (
  transactions: Transaction[],
  userId: string
): ExpenseBreakdown[] => {
  const now = new Date();
  const startOfCurrentMonth = startOfMonth(now);
  const endOfCurrentMonth = endOfMonth(now);

  // First try to get current month expenses
  let monthlyExpenses = transactions.filter(
    (t) =>
      t.userId === userId &&
      t.type === "expense" &&
      t.category !== "Debt" && // Exclude debt transactions
      new Date(t.date) >= startOfCurrentMonth &&
      new Date(t.date) <= endOfCurrentMonth
  );

  // If current month has no expenses, use last 3 months
  if (monthlyExpenses.length === 0) {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    monthlyExpenses = transactions.filter(
      (t) =>
        t.userId === userId &&
        t.type === "expense" &&
        t.category !== "Debt" && // Exclude debt transactions
        new Date(t.date) >= threeMonthsAgo
    );
  }

  const categoryTotals = monthlyExpenses.reduce((acc, t) => {
    acc[t.category] = (acc[t.category] || 0) + t.amount;
    return acc;
  }, {} as Record<string, number>);

  const totalExpenses = Object.values(categoryTotals).reduce(
    (sum, amount) => sum + amount,
    0
  );

  return Object.entries(categoryTotals).map(([category, amount]) => ({
    category,
    amount,
    percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0,
    color: getCategoryColor(category),
  }));
};

export const calculateRunwayProjection = (
  currentBalance: number,
  monthlyIncome: number,
  monthlyExpenses: number,
  months: number = 60
): RunwayProjection[] => {
  const projections: RunwayProjection[] = [];
  let balance = currentBalance;

  // Add current month (month 0)
  projections.push({
    month: 0,
    balance,
    runwayMonths: monthlyExpenses > 0 ? balance / monthlyExpenses : Infinity,
  });

  for (let i = 1; i <= months; i++) {
    balance = balance + monthlyIncome - monthlyExpenses;
    const runwayMonths =
      monthlyExpenses > 0 ? balance / monthlyExpenses : Infinity;

    projections.push({
      month: i,
      balance,
      runwayMonths,
    });
  }

  return projections;
};

export const calculatePercentageChange = (
  current: number,
  previous: number
): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

const getCategoryColor = (category: string): string => {
  const colors = {
    Housing: "#10b981", // Primary emerald green
    Groceries: "#059669", // Darker emerald
    Medical: "#ef4444", // Red
    Transportation: "#f59e0b", // Amber
    Entertainment: "#8b5cf6", // Purple
    Utilities: "#06b6d4", // Cyan
    Salary: "#10b981", // Primary emerald green
    Freelance: "#059669", // Darker emerald
    Investment: "#f59e0b", // Amber
    Other: "#6b7280", // Gray
  };

  return colors[category as keyof typeof colors] || "#6b7280";
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const formatPercentage = (percentage: number): string => {
  return `${percentage >= 0 ? "+" : ""}${percentage.toFixed(1)}%`;
};
